/**
 * Dev-only knobs for observing the FolderItemList behavior locally.
 *
 * ALL values here are for local development only and are easy to remove.
 * Pages read `DEV_FOLDER_CHUNK_SIZE` / `DEV_ITEM_PAGE_SIZE` (when `import.meta.env.DEV`)
 * to shrink page sizes so the background chunking and item load-more are visible.
 *
 * The Apollo `delayLink` latency constant lives with the link chain in
 * `packages/graphql-client` (that package can't import from apps/web).
 */

/** Uniform Apollo link latency (ms) when VITE_DEV_FAKE_DELAY=1 (see App.tsx). */
export const DEV_FAKE_DELAY_MS = 1500;

/** Folder page/chunk size in dev, to watch sequential chunking. */
export const DEV_FOLDER_CHUNK_SIZE = 20;

/** Item page size in dev, to watch scroll load-more fire sooner. */
export const DEV_ITEM_PAGE_SIZE = 5;

/**
 * Folder FOREGROUND page size (production) — small for a fast first paint;
 * the background fill takes over immediately after.
 */
export const FOLDER_PAGE_SIZE = 50;

/**
 * Folder BACKGROUND-fill chunk size (production) — `first` on each fetchMore
 * chunk. Large so big accounts drain in few sequential round-trips
 * (e.g. 800 folders = 3 chunks).
 */
export const FOLDER_CHUNK_SIZE = 250;

/** True when the dev fake-delay switch is on (also shrinks page sizes). */
function devTuningOn(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_DEV_FAKE_DELAY === '1';
}

/** Folder foreground page size, shrunk under the dev switch. */
export function resolveFolderPageSize(): number {
  return devTuningOn() ? DEV_FOLDER_CHUNK_SIZE : FOLDER_PAGE_SIZE;
}

/** Folder background chunk size (passed as `first` on each background fetchMore). */
export function resolveFolderChunkSize(): number {
  return devTuningOn() ? DEV_FOLDER_CHUNK_SIZE : FOLDER_CHUNK_SIZE;
}

/** Item page size, shrunk under the dev switch. */
export function resolveItemPageSize(defaultSize: number): number {
  return devTuningOn() ? DEV_ITEM_PAGE_SIZE : defaultSize;
}
